import Swal from 'sweetalert2'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'

import {
  digObject, formatDate, launchModal, stringToTitleCase,
} from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as invoiceActions from '@redux/modules/invoice'
import type { DeleteParams } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import PageContext from '@contexts/pageContext'

import { baseURL } from '@functions/api'
import { getInvoiceDownloadUrl } from '@functions/invoice'
import handleCallbackAction from '@functions/handleCallbackAction'
import type { ConfirmActionParams } from '@functions/entity'

import defaultFormState, { requiredFields } from '@models/invoice'
import type {
  CampaignModel, InvoiceModel, InvoiceRequestOptions, UserModel,
} from '@models/types'
import { getCampaignPermissions, getCampaignType } from '@functions/campaign'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (invoice: Partial<InvoiceModel>, options?: GenerateUrlsOptions) => {
  const { campaign_id, id } = invoice || {}

  const { prefixRoute = 'systemManager' } = options || {}
  const isSystemManager = prefixRoute === 'systemManager'

  return {
    approvedInvoicesUrl: `#/${prefixRoute}/invoices?approved=true`,
    editInvoiceDetailsUrl: `#/${prefixRoute}/invoices/${id}/edit/details`,
    editInvoiceUrl: isSystemManager ? `#/${prefixRoute}/invoices/${id}/edit/`
      : `#/${prefixRoute}/invoices/${id}`,
    editCampaignInvoiceUrl: `#/campaigns/${campaign_id}/edit/accounting/invoices/${id}`,
    invoicesIndexUrl: `#/${prefixRoute}/invoices`,
    pdfDownloadUrl: `${baseURL}/invoices/${id}/pdf`,
    unapprovedInvoicesUrl: `#/${prefixRoute}/invoices?approved=false`,
  }
}

type ConfirmParamsType = ConfirmActionParams & {
  callbacks: {
    beforeAction?: () => void,
  },
}

export const handleCallbackActionWithConfirmation = (params: ConfirmParamsType) => {
  const { callbacks, options, strings } = params

  const {
    actionFn, beforeAction, ...restCallbacks
  } = callbacks
  const {
    actionString,
    confirmString = 'Are you sure?',
    entityName = 'Invoice',
  } = strings

  const {
    confirmButtonColor = '#43AC6A',
    entityParams,
    icon = 'warning',
  } = options || {}

  const title = `${actionString} ${entityName}?` // Delete Agency?
  const confirmButtonText = `Yes, ${actionString} it.` // Yes, delete it.

  const payload = {
    callbacks: {
      action: actionFn,
      ...restCallbacks,
    },
    entityParams,
    toastText: 'Invoice Updated',
  }

  Swal
    .fire({
      confirmButtonColor,
      confirmButtonText,
      icon,
      showCancelButton: true,
      text: confirmString,
      title: stringToTitleCase(title),
    })
    .then(({ value }) => {
      if (value){
        if (beforeAction){
          beforeAction()
        }

        // @ts-expect-error TS2345
        handleCallbackAction(payload)
      }
    })
}

type CreateInvoiceParams = {
  dispatch: AppDispatch,
  isAgencyUser: boolean,
  invoiceParams: Partial<InvoiceModel>,
  requestOptions?: InvoiceRequestOptions,
}

const createInvoice = (params: CreateInvoiceParams) => {
  const {
    dispatch, isAgencyUser, invoiceParams, requestOptions,
  } = params
  const { createInvoice: createFn } = invoiceActions

  return dispatch(createFn(invoiceParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      const { editCampaignInvoiceUrl, editInvoiceUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: isAgencyUser ? editCampaignInvoiceUrl : editInvoiceUrl,
      }
    }

    return response
  })
}

type CreateInvoiceFromItemsParams = {
  campaign?: Partial<CampaignModel>,
  campaignItemIds: number[],
  currentUser?: Partial<UserModel>,
  dispatch: AppDispatch,
  invoice: Partial<InvoiceModel>,
  orderIds: number[],
  requestOptions?: InvoiceRequestOptions,
}

const createInvoiceFromItems = (params: CreateInvoiceFromItemsParams) => {
  const {
    campaign,
    campaignItemIds,
    currentUser,
    dispatch,
    invoice,
    orderIds,
    requestOptions,
  } = params

  const { createCampaignInvoice: createFn } = invoiceActions

  // @ts-expect-error TS2339
  const campaignPermissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })
  const canManageInvoices = campaignPermissions?.invoices?.manage ?? false

  const prefixUrl = `#/campaigns/${invoice.campaign_id}/edit`

  return dispatch(createFn({ campaignItemIds, invoice, orderIds }, requestOptions)).then((response) => {
    // @ts-expect-error TS2339
    const { data, success } = response

    if (!success){
      return response
    }

    return {
      ...response,
      redirectUrl: canManageInvoices
        ? `${prefixUrl}/accounting/invoices/${data?.id}`
        : undefined,
    }
  })
}

type DeleteInvoiceParams = {
  invoice: DeleteParams<InvoiceModel>,
  dispatch: AppDispatch,
}

const deleteInvoice = (params: DeleteInvoiceParams) => {
  const { dispatch, invoice } = params
  const { deleteInvoice: deleteFn } = invoiceActions

  return dispatch(deleteFn(invoice))
}

type UpdateInvoiceParams = {
  dispatch: AppDispatch,
  invoice: InvoiceModel,
  invoiceParams: Partial<InvoiceModel>,
  requestOptions?: InvoiceRequestOptions,
}

const updateInvoice = (params: UpdateInvoiceParams) => {
  const {
    dispatch, invoice, invoiceParams, requestOptions,
  } = params
  const { updateInvoice: updateFn } = invoiceActions

  const updatedParams = {
    id: invoice.id,
    ...invoiceParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}
type ImportInvoiceParams = {
  agency_id: number,
  campaign_id: number,
  external_invoice_id: string,
}

type ImportExternalInvoiceParams = {
  dispatch: AppDispatch,
  importInvoiceParams: ImportInvoiceParams,
  requestOptions?: InvoiceRequestOptions,
}

const importExternalInvoice = (params: ImportExternalInvoiceParams) => {
  const {
    importInvoiceParams, dispatch, requestOptions,
  } = params
  const { importExternalInvoice: importFn } = invoiceActions

  return dispatch(importFn(importInvoiceParams, requestOptions))
}

type ApproveInvoiceParams = {
  dispatch: AppDispatch,
  invoice: InvoiceModel,
  requestOptions?: InvoiceRequestOptions,
}

const approveInvoice = (params: ApproveInvoiceParams) => {
  const { dispatch, invoice, requestOptions } = params

  const { approveInvoice: approveFn } = invoiceActions
  return dispatch(approveFn(invoice, requestOptions))
}

type CreateInvoiceOnAccountingApiParams = {
  dispatch: AppDispatch,
  invoice: InvoiceModel,
  requestOptions?: InvoiceRequestOptions,
}

const createInvoiceOnAccountingApi = (params: CreateInvoiceOnAccountingApiParams) => {
  const { dispatch, invoice, requestOptions } = params

  const { createExternalInvoice: createFn } = invoiceActions
  return dispatch(createFn(invoice, requestOptions))
}

type VoidInvoiceParams = {
  dispatch: AppDispatch,
  invoice: InvoiceModel,
  requestOptions?: InvoiceRequestOptions,
}

const voidInvoice = (params: VoidInvoiceParams) => {
  const { dispatch, invoice, requestOptions } = params

  const { voidInvoice: voidFn } = invoiceActions
  return dispatch(voidFn(invoice, requestOptions))
}

type SyncExternalInvoiceParams = {
  dispatch: AppDispatch,
  invoice: InvoiceModel,
  requestOptions?: InvoiceRequestOptions,
}

const syncExternalInvoice = (params: SyncExternalInvoiceParams) => {
  const { dispatch, invoice, requestOptions } = params

  const { syncExternalInvoice: syncFn } = invoiceActions
  return dispatch(syncFn(invoice, requestOptions))
}

type SyncExternalInvoicesParams = {
  syncParams: {
      agencyId: number,
      forceSync?: boolean,
  },
  dispatch: AppDispatch,
  requestOptions?: InvoiceRequestOptions,
}

const syncExternalInvoices = (params: SyncExternalInvoicesParams) => {
  const {
    syncParams, dispatch, requestOptions,
  } = params

  const { syncExternalInvoices: syncFn } = invoiceActions
  return dispatch(syncFn(syncParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

const buildInvoiceState = (invoice: Partial<InvoiceModel>) => {
  const { dates } = invoice

  // @ts-expect-error TS2345
  const dueDate = digObject(dates, 'due.date', '')
  const formattedDueDate = formatDate(dueDate, 'dd/MM/yyyy')

  const formattedInvoiceEntityState = {
    due_date: formattedDueDate,
    ...invoice,
  }

  return formattedInvoiceEntityState
}

export function useInvoiceForm(
  invoice: Partial<InvoiceModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const invoiceForm = useForm(
    defaultFormState,
    {
      entity: buildInvoiceState(invoice),
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [invoice.id, invoice.cache_key],
  )

  return {
    ...invoiceForm,
  }
}

// @ts-expect-error TS2740
export function useRelations(invoice: InvoiceModel = {}) {
  const { campaign_id, debtor_id, debtor_type } = invoice

  const entities = useSelector(reduxState => reduxState.entities)

  const {
    agencies, brands, campaigns, providers, bookings, providerPackages,
  } = entities

  // @ts-expect-error TS2538
  const agency = debtor_type === 'Agency' && agencies[debtor_id]
  // @ts-expect-error TS2538
  const brand = debtor_type === 'Brand' && brands[debtor_id]
  // @ts-expect-error TS2538
  const provider = debtor_type === 'Provider' && providers[debtor_id]

  const bookingId = digObject(invoice, 'data.booking_id', '')
  const providerPackageId = digObject(invoice, 'data.package_id', '')

  const booking = bookings[bookingId]
  const providerPackage = providerPackages[providerPackageId]

  const campaign = campaign_id ? campaigns[campaign_id] || {} : {}

  return {
    booking,
    campaign,
    debtor: agency || brand || provider || {},
    providerPackage,
  }
}

function useInvoice(initEntity: Partial<InvoiceModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: invoice }: { entity: InvoiceModel} = useLatestEntity(initEntity, 'invoices')

  const currentUserPayload = useCurrentUser()
  const { isAgencyUser, currentUser } = currentUserPayload

  const dispatch = useDispatch()

  const {
    debtor, campaign, booking, providerPackage,
  } = useRelations(invoice)

  const {
    creating, loading, updating,
  } = useSelector(reduxState => reduxState.invoices)

  const { callbacks } = useContext(PageContext)

  const leadAgentUserId = digObject(invoice, 'data.lead_agent.user_id', '')
  const supportTeamUserIds = digObject(invoice, 'data.support_team.user_ids', [])
  const supportTeamUserRoles = digObject(invoice, 'data.support_team.user_roles', [])

  const hasUsers = !!(leadAgentUserId !== '' || supportTeamUserIds.length > 0)

  const invoiceAttachments = useSelector(reduxState => reduxState.entities.invoiceAttachments)
  const downloadUrl = getInvoiceDownloadUrl({ invoice, invoiceAttachments })

  const hasLinkedEntities = (
    providerPackage !== undefined
    || booking !== undefined
    // @ts-expect-error TS2339
    || campaign?.id !== undefined
    || hasUsers)

  return {
    booking,
    callbacks: {
      approveInvoice: (entityOptions?: InvoiceRequestOptions) => (
        approveInvoice({ invoice, dispatch, requestOptions: entityOptions })
      ),
      createInvoice: (invoiceParams: Partial<InvoiceModel>, entityOptions?: InvoiceRequestOptions) => (
        createInvoice({
          isAgencyUser, invoiceParams, dispatch, requestOptions: entityOptions,
        })
      ),
      createInvoiceFromItems: (
        params: { campaignItemIds: number[], orderIds: number[] },
        entityOptions?: InvoiceRequestOptions,
      ) => (
        createInvoiceFromItems({
          campaign,
          campaignItemIds: params.campaignItemIds,
          currentUser,
          dispatch,
          invoice,
          orderIds: params.orderIds,
          requestOptions: entityOptions,
        })
      ),
      createAgencyPayableInvoice: () => launchModal({
        callbacks,
        modalKey: 'CreateAgencyPayableInvoiceModal',
        payload: { invoice },
      }),
      createOrEditInvoice: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditInvoiceModal',
        payload: { invoice },
      }),
      createAdminInvoice: () => launchModal({
        callbacks,
        modalKey: 'CreateAdminInvoiceModal',
        payload: { invoice },
      }),
      createInvoiceOnAccountingApi: (entityOptions?: InvoiceRequestOptions) => (
        createInvoiceOnAccountingApi({
          dispatch,
          invoice,
          requestOptions: entityOptions,
        })
      ),
      deleteInvoice: () => deleteInvoice({ dispatch, invoice }),
      handleCallbackActionWithConfirmation: (params: ConfirmParamsType) => handleCallbackActionWithConfirmation(params),
      importExternalInvoice: (importInvoiceParams: ImportInvoiceParams) => importExternalInvoice({
        importInvoiceParams,
        dispatch,
      }),
      launchInvoiceCampaignModal: () => launchModal({
        callbacks,
        modalKey: 'ManageInvoiceCampaignModal',
        payload: { invoice, campaign },
      }),
      manageInvoiceBillingDetails: () => launchModal({
        callbacks,
        modalKey: 'ManageInvoiceBillingDetailsModal',
        payload: { invoice },
      }),
      manageInvoiceDebtor: () => launchModal({
        callbacks,
        modalKey: 'ManageInvoiceDebtorModal',
        payload: { invoice },
      }),
      updateInvoice: (invoiceParams: Partial<InvoiceModel>, entityOptions?: InvoiceRequestOptions) => (
        updateInvoice({
          invoice, invoiceParams, dispatch, requestOptions: entityOptions,
        })
      ),
      syncExternalInvoice: (entityOptions?: InvoiceRequestOptions) => (
        syncExternalInvoice({
          dispatch,
          invoice,
          requestOptions: entityOptions,
        })
      ),
      syncExternalInvoices: (
        syncParams: {
          agency_id: number,
          force_sync?: boolean,
        },
        entityOptions?: InvoiceRequestOptions,
      ) => (
        syncExternalInvoices({
          dispatch,
          requestOptions: entityOptions,
          // @ts-expect-error TS2741
          syncParams,
        })
      ),
      voidInvoice: (entityOptions?: InvoiceRequestOptions) => (
        voidInvoice({
          dispatch,
          invoice,
          requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    creating,
    debtor,
    downloadUrl,
    hasLinkedEntities,
    hasUsers,
    invoice,
    leadAgentUserId,
    loading,
    providerPackage,
    supportTeamUserIds,
    supportTeamUserRoles,
    updating,
    urls: generateUrls(invoice),
  }
}

export type UseInvoicePayload = ReturnType<typeof useInvoice>

export default useInvoice
